import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, X } from 'lucide-react';

export interface SelectOption { label: string; value: string }

interface MultiDropdownSelectProps {
  values: string[];
  onChange: (v: string[]) => void;
  options: SelectOption[];
  placeholder: string;
  accentColor?: string;
  label?: string;
}

const MultiDropdownSelect: React.FC<MultiDropdownSelectProps> = ({
  values, onChange, options, placeholder, accentColor = '#2a52a8', label,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val: string) => {
    if (values.includes(val)) {
      onChange(values.filter(v => v !== val));
    } else {
      onChange([...values, val]);
    }
  };

  const isActive = values.length > 0;

  // Label affiché dans le trigger
  const triggerLabel = () => {
    if (values.length === 0) return placeholder;
    if (values.length === 1) {
      return options.find(o => o.value === values[0])?.label || values[0];
    }
    return `${values.length} sélectionnés`;
  };

  return (
    <div className="flex-1 min-w-[200px]" ref={ref}>
      {label && (
        <label className="block text-xs font-semibold mb-1.5" style={{ color: '#6b7280' }}>
          {label}
        </label>
      )}
      <div className="relative">
        {/* Trigger */}
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-white text-sm font-medium transition-all focus:outline-none"
          style={{
            border: `2px solid ${isActive ? accentColor : '#e5e7eb'}`,
            color: isActive ? '#111827' : '#9ca3af',
            boxShadow: open ? `0 0 0 3px ${accentColor}20` : 'none',
          }}
        >
          <span className="truncate flex items-center gap-1.5">
            {isActive && (
              <span
                className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold text-white flex-shrink-0"
                style={{ background: accentColor }}
              >
                {values.length}
              </span>
            )}
            {triggerLabel()}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
            {isActive && (
              <span
                onClick={e => { e.stopPropagation(); onChange([]); }}
                className="p-0.5 rounded hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <X size={12} style={{ color: '#9ca3af' }} />
              </span>
            )}
            <ChevronDown
              size={14}
              className="transition-transform duration-200"
              style={{
                color: isActive ? accentColor : '#9ca3af',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </div>
        </button>

        {/* Dropdown */}
        {open && (
          <div
            className="absolute z-50 w-full mt-1.5 bg-white rounded-xl border border-gray-100 py-1 overflow-auto"
            style={{
              maxHeight: 240,
              boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
              animation: 'dropdownIn 0.15s ease-out',
            }}
            onMouseDown={e => e.stopPropagation()}
          >
            <style>{`
              @keyframes dropdownIn {
                from { opacity: 0; transform: translateY(-6px); }
                to   { opacity: 1; transform: translateY(0); }
              }
            `}</style>

            {/* Option "tout désélectionner" */}
            {isActive && (
              <>
                <div
                  onClick={() => onChange([])}
                  className="flex items-center justify-between px-4 py-2 text-xs cursor-pointer transition-colors text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                >
                  Tout désélectionner
                </div>
                <div className="my-1 border-t border-gray-100" />
              </>
            )}

            {options.map(opt => {
              const isSelected = values.includes(opt.value);
              return (
                <div
                  key={opt.value}
                  onClick={() => toggle(opt.value)}
                  className="flex items-center justify-between px-4 py-2 text-sm cursor-pointer transition-colors"
                  style={{
                    background: isSelected ? `${accentColor}12` : 'transparent',
                    color: isSelected ? accentColor : '#374151',
                    fontWeight: isSelected ? 600 : 400,
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f9fafb'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span>{opt.label}</span>
                  {isSelected && <Check size={13} style={{ color: accentColor }} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiDropdownSelect;
